import { Dropdown as BaseDropdown } from "@netlify/ui";
import type * as React from "react";
import { forwardRef, useMemo } from "react";
import { cn } from "../classnames.js";

export const getInitialSelectedOption = <T,>(
  options: DropdownOption<T>[],
  initial?: DropdownOption<T> | number,
): DropdownOption<T> | undefined => {
  if (typeof initial === "number") {
    return options[initial];
  }
  if (typeof initial === "object") {
    return initial;
  }
  return options[0];
};

const getClassNamesForVariant = (
  variant: Exclude<DropdownOption["variant"], undefined>,
): string[] => {
  switch (variant) {
    case "danger":
      return ["danger"];
    case "standard":
      return [];
  }
};

export type DropdownOption<T = unknown> = {
  /**
   * A human-readable element rendered to represent the option.
   */
  label: React.ReactNode;

  /**
   * The programmatic value used when the option is selected.
   */
  value?: T | undefined;

  /**
   *
   *
   * @default standard
   */
  variant?: "standard" | "danger" | undefined;
};

export type DropdownProps<T> = {
  /**
   * A class property to attach to the element.
   *
   * @see {@link !Element.className}
   */
  className?: string | undefined;

  /**
   * If `true`, the component is disabled.
   *
   * @default false
   */
  disabled?: boolean | undefined;

  /**
   * An option or an index of an option from `options` to select on first render.
   */
  initialSelectedOption?: DropdownOption<T> | number | undefined;

  /**
   * A callback invoked when an option is selected.
   */
  onChange?: ((option: DropdownOption<T> | null) => void) | undefined;

  /**
   * A list of options to render in the dropdown.
   */
  options?: DropdownOption<T>[] | undefined;

  /**
   * A visual element rendered next to the current selected option in the dropdown menu. To disable
   * the indicator, specify `null`.
   *
   * Currently, only disabling the indicator is supported.
   */
  selectedOptionIndicator?: null | undefined;

  /**
   * Display a static value in place of the the dropdown toggle button (in place of the current
   * selected option).
   */
  toggle?: { label?: React.ReactNode | undefined } | undefined;
};

/**
 * Dropdowns let users select from a list of options.
 *
 * <!-- TODO(ndhoule): Add a screenshot here -->
 *
 * @param props
 * @internal
 * @example
 * ```ts
 * import { Dropdown } from "@netlify/sdk/react/components";
 *
 * <Dropdown
 *   options=[
 *     { label: "Pomeranian", value: "pomeranian" },
 *     { label: "Golden Retriever", value: "golden-retriever" },
 *     { label: "Doge", value: "shiba-inu" },
 *   ]
 * />
 * ```
 */
export const Dropdown = forwardRef(function Dropdown<T>(
  {
    initialSelectedOption: originalInitialSelectedOption,
    onChange,
    options: originalOptions = [],
    selectedOptionIndicator: selectedItemIndicator,
    ...otherProps
  }: DropdownProps<T>,
  ref: React.ForwardedRef<HTMLFormElement>,
) {
  const options = useMemo<DropdownOption<T>[]>(
    () =>
      originalOptions.map((originalOption) => {
        return {
          ...originalOption,
          className: cn(
            getClassNamesForVariant(originalOption.variant ?? "standard"),
          ),
          label:
            originalOption.label === "" ? <>&nbsp;</> : originalOption.label,
        };
      }),
    [originalOptions],
  );

  return (
    <BaseDropdown
      // @ts-expect-error XXX(ndhoule): The Dropdown component technically accepts a ref, but it's
      // not part of its public props interface.
      ref={ref}
      initialSelectedItem={getInitialSelectedOption(
        options,
        originalInitialSelectedOption,
      )}
      items={options}
      noCheck={selectedItemIndicator === null}
      onSelect={onChange}
      {...otherProps}
    />
  );
}) as <T>(
  props: DropdownProps<T> & {
    ref?: React.ForwardedRef<HTMLElement> | undefined;
  },
) => React.JSX.Element;
